import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";

import { AnalyticsEventType } from "@/constants/analytics";
import { DynamicSupplyHardware, SimpleClusterForm } from "@/types";
import { trackEvent } from "@/utils/analytics";
import { useContainerStore } from "@/store/simpleCluster";

import { DISPLAY_LIMIT, FIXED_FIRST_RECIPE, RECIPES } from "../steps/RecipesSection.constants";
import {
  applyRecipeToFormValues,
  findHardwareMatchForRecipe,
  isRecipeAvailableInInventory,
  isRecipeSoldOutInInventory,
  recipeAvailabilityLabel
} from "../steps/RecipesSection.utils";

import { containerVerticalFlowAnalytics } from "../analytics";

export const useRecipesSection = () => {
  const { values, setFieldValue, setValues } = useFormikContext<SimpleClusterForm>();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const fetchHardwareWithFilters = useContainerStore(
    (state) => state.fetchDynamicSupplyHardwareAndFilters
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showHardwareSelectionWarning, setShowHardwareSelectionWarning] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState<string | null>(null);
  const [isHardwareInventoryLoading, setIsHardwareInventoryLoading] = useState(
    () => !Boolean(values.createClusterData?.hardwareList?.length)
  );
  const [visibleRecipeKeys, setVisibleRecipeKeys] = useState<string[]>(() =>
    Object.keys(RECIPES).slice(0, DISPLAY_LIMIT)
  );

  const hardwareList = useMemo(() => {
    return values.createClusterData?.hardwareList as DynamicSupplyHardware[] | undefined;
  }, [values.createClusterData]);

  // Prefetch inventory on the recipes step so tiles can disable before visiting hardware.
  useEffect(() => {
    if (hardwareList?.length) {
      setIsHardwareInventoryLoading(false);
      return;
    }

    setIsHardwareInventoryLoading(true);
    const abortController = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const { hardware } = await fetchHardwareWithFilters({ abortController });
        if (cancelled || abortController.signal.aborted) return;

        const current = valuesRef.current;
        const existing = (
          current.createClusterData as { hardwareList?: DynamicSupplyHardware[] } | undefined
        )?.hardwareList;
        if (Array.isArray(existing) && existing.length > 0) {
          setIsHardwareInventoryLoading(false);
          return;
        }

        if (hardware?.length) {
          setFieldValue("createClusterData", {
            ...(current.createClusterData || {}),
            hardwareList: hardware
          });
          // Keep the preloader until Formik has hardwareList (cleared on effect re-run above).
          return;
        }

        setIsHardwareInventoryLoading(false);
      } catch {
        if (!cancelled) setIsHardwareInventoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [fetchHardwareWithFilters, hardwareList?.length, setFieldValue]);

  const allProcessedRecipes = useMemo(() => {
    return Object.entries(RECIPES).map(([key, recipe]) => {
      const matchedHardware = findHardwareMatchForRecipe(hardwareList, recipe);
      const dynamicStorage = matchedHardware?.storage
        ? `${matchedHardware.storage} GB`
        : recipe.storage;
      const isCompatible = isRecipeAvailableInInventory(hardwareList, recipe);
      const isSoldOut = isRecipeSoldOutInInventory(hardwareList, recipe);

      return {
        key,
        ...recipe,
        isSoldOut,
        storage: dynamicStorage,
        isCompatible,
        availabilityLabel: recipeAvailabilityLabel(recipe)
      };
    });
  }, [hardwareList]);

  const finalizeRecipeSelection = useCallback(
    (recipeKey: string) => {
      const recipe = RECIPES[recipeKey];
      if (recipe) {
        const newValues = applyRecipeToFormValues(values, recipe);
        setValues({ ...newValues, containerRecipe: recipeKey });
      } else {
        setFieldValue("containerRecipe", recipeKey);
      }

      trackEvent(AnalyticsEventType.CLUSTER_RECIPE_SELECTED, {
        recipe: recipeKey,
        clusterType: "container"
      });

      setVisibleRecipeKeys((prevKeys) => {
        if (prevKeys.includes(recipeKey)) {
          return prevKeys;
        }
        return [
          FIXED_FIRST_RECIPE,
          recipeKey,
          ...prevKeys
            .filter((key) => key !== FIXED_FIRST_RECIPE && key !== recipeKey)
            .slice(0, DISPLAY_LIMIT - 2)
        ];
      });
    },
    [setFieldValue, setValues, values]
  );

  const onSelect = useCallback(
    (recipeKey: string) => {
      const recipe = RECIPES[recipeKey];
      if (!recipe) return;
      // Disable based on inventory availability — not the currently selected GPU.
      if (!isRecipeAvailableInInventory(hardwareList, recipe)) return;

      finalizeRecipeSelection(recipeKey);
    },
    [finalizeRecipeSelection, hardwareList]
  );

  const onDialogRecipeRowClick = useCallback(
    (recipeKey: string) => {
      const recipe = allProcessedRecipes.find((r) => r.key === recipeKey);
      if (!recipe || recipe.isSoldOut || !recipe.isCompatible) return;
      onSelect(recipeKey);
      setIsDialogOpen(false);
    },
    [allProcessedRecipes, onSelect]
  );

  const onCloseWarning = useCallback(() => {
    setShowHardwareSelectionWarning(false);
    setPendingRecipe(null);
  }, []);

  const onConfirmWarning = useCallback(() => {
    if (pendingRecipe) {
      finalizeRecipeSelection(pendingRecipe);
    }
    setShowHardwareSelectionWarning(false);
    setPendingRecipe(null);
  }, [finalizeRecipeSelection, pendingRecipe]);

  useEffect(() => {
    if (isDialogOpen) {
      containerVerticalFlowAnalytics.trackRecipeModalOpened();
    }
  }, [isDialogOpen]);

  const visibleRecipes = useMemo(() => {
    return visibleRecipeKeys
      .map((key) => allProcessedRecipes.find((r) => r.key === key))
      .filter((recipe): recipe is NonNullable<typeof recipe> => !!recipe?.name);
  }, [visibleRecipeKeys, allProcessedRecipes]);

  return {
    values,
    isDialogOpen,
    setIsDialogOpen,
    visibleRecipes,
    allProcessedRecipes,
    isHardwareInventoryLoading,
    onSelect,
    onDialogRecipeRowClick,
    onCloseWarning,
    onConfirmWarning,
    showHardwareSelectionWarning
  };
};
